'use strict';
import axios from 'axios';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import persist from '@alpinejs/persist';
import morph from '@alpinejs/morph';
import { initAlpineStores } from './alpine/stores';
import { initAlpineMagics } from './alpine/magics';
import { qSel, showError, initGlobalSwalAlerts, swalToast, swalSuccess, swalError, swalWarning, swalConfirm } from "@modernman00/shared-js-lib";
import Swal from 'sweetalert2';
import PWAManager from './components/pwa/pwaManager';
import { initDarkMode } from './components/darkMode';
import { initBackToTop } from './components/backToTop';
import { initVisualSupremacy } from './components/vse-canvas';
import { initFamilyTooltip } from './components/familyTooltip';
import './components/recommendModal';
import './components/navbar';

import { profileFeed } from './components/profilePage/feedComponent';
import { profileSidebar, upcomingEvents } from './components/profilePage/sidebarComponents';

initGlobalSwalAlerts();
window.Swal = Swal;
window.swal = Swal;
window.swalToast = swalToast;
window.swalSuccess = swalSuccess;
window.swalError = swalError;
window.swalWarning = swalWarning;
window.swalConfirm = swalConfirm;
window.Alpine = Alpine;

Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.plugin(persist);
Alpine.plugin(morph);

initAlpineStores(Alpine);
initAlpineMagics(Alpine);

Alpine.data('profileFeed', profileFeed);
Alpine.data('profileSidebar', profileSidebar);
Alpine.data('upcomingEvents', upcomingEvents);
window.profileFeed = profileFeed;
window.profileSidebar = profileSidebar;
window.upcomingEvents = upcomingEvents;

window.pwaManager = new PWAManager();
initDarkMode();
initBackToTop();
initVisualSupremacy();
initFamilyTooltip();
let routePromise = Promise.resolve();

// The server occasionally finds the session's CSRF token missing (e.g. session
// data expired/evicted between page load and this request) and responds 401
// with "We are not familiar with the nature of your activities.". That same
// response always carries a fresh Set-Cookie: XSRF-TOKEN, which the browser
// applies immediately — so a single automatic retry of the exact same request
// picks up the new cookie and succeeds transparently instead of surfacing a
// dead-end error the user has to work around by reloading the page.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config;
    const message = error.response?.data?.message;
    const isStaleCsrfToken = error.response?.status === 401
      && typeof message === 'string'
      && message.includes('not familiar with the nature of your activities')
      && config && !config._csrfRetried;

    if (isStaleCsrfToken) {
      config._csrfRetried = true;
      if (config.headers) {
        delete config.headers['X-XSRF-TOKEN'];
        delete config.headers['x-xsrf-token'];
        delete config.headers['X-CSRF-TOKEN'];
        delete config.headers['x-csrf-token'];
      }
      return axios(config);
    }

    return Promise.reject(error);
  }
);

/**
 * Tests if the current URL matches the given route.
 *
 * @param {string} url A URL route to test.
 *
 * @returns {boolean} True if the URL matches, otherwise false.
 */
const checkURL = (url) =>
  !!window.location.pathname.match(new RegExp(`^/${url}(?:/[^/]+)*$`));

try {
  const $navbarBurgers = Array.prototype.slice.call(
    document.querySelectorAll('.navbar-burger'),
    0,
  );

  // Add a click event on each of them
  $navbarBurgers.forEach((el) => {
    el.addEventListener('click', () => {
      // Get the target from the "data-target" attribute
      const target = el.dataset.target;
      const $target = document.getElementById(target);

      // Toggle the "is-active" class on both the "navbar-burger" and the "navbar-menu"
      el.classList.toggle('is-active');
      $target.classList.toggle('is-active');
    });
  });
} catch (error) {
  showError(error);
}

// Get all "navbar-burger" elements

if (window.location.pathname === '/register') {
  if (qSel('.registerNav')) qSel('.registerNav').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'register' */
    './components/register/'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/allMembers') {
  if (qSel('.allMemberNav')) qSel('.allMemberNav').style.display = 'none';
  if (qSel('.allMembersNav')) qSel('.allMembersNav').style.display = 'none';

  routePromise = import(
    /* webpackChunkName: 'all_members' */
    './components/allMembers/'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname.toLowerCase().startsWith('/reels')) {
  if (qSel('.reelsNav')) qSel('.reelsNav').style.display = 'none';

  routePromise = import(
    /* webpackChunkName: 'reels' */
    './components/reels/reelsPlayer'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (
  window.location.pathname === '/login' 
) {
  if (qSel('.loginNav')) qSel('.loginNav').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'login' */
    './components/acctMgt/login'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (
  window.location.pathname === '/lasu'
) {
  if (qSel('.loginNav')) qSel('.loginNav').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'adminLogin' */
    './components/acctMgt/adminLogin'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (
  window.location.pathname.startsWith('/portal_') ||
  window.location.pathname.startsWith('/admin-') ||
  (window.location.pathname.includes('/2fa') && !window.location.pathname.startsWith('/login'))
) {
  // Dynamic ADMIN_SECRET_PATH routes — load adminAuth component
  routePromise = import(
    /* webpackChunkName: 'adminAuth' */
    './components/acctMgt/adminAuth'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/login/forgot') {
  if (qSel('.signup_login')) qSel('.signup_login').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'forgotPwd' */
    './components/forgotPwd/'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (['/login/code', '/verify-email'].includes(window.location.pathname)) {
  if (qSel('.signup_login')) qSel('.signup_login').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'code' */
    './components/acctMgt/code'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (
  window.location.pathname.toLowerCase().startsWith('/member/profilepage') ||
  window.location.pathname.toLowerCase().startsWith('/profilepage') ||
  checkURL('member/ProfilePage') ||
  checkURL('member/profilePage') ||
  checkURL('profilePage')
) {
  if (qSel('.profilePageNav')) qSel('.profilePageNav').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'profilePage' */
    './components/profilePage/'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/login/changePW' || window.location.pathname === '/changePW') {
  if (qSel('.login')) qSel('.login').style.display = 'none'; // navbar mgt
  if (qSel('.signUp')) qSel('.signUp').style.display = 'none'; // navbar mgt
  // qSel('#loader').style.display ="none" // loader
  routePromise = import(
    /* webpackChunkName: 'changePW' */
    './components/changePW/'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/profilepage/img') {
  // qSel('.login').style.display ="none" // navbar mgt
  routePromise = import(
    /* webpackChunkName: 'img' */
    './components/profilePage/imgViewer'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/createFamilyCode') {
  routePromise = import(
    /* webpackChunkName: 'familyCode' */
    './components/register/familyCode'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/register/nextStep') {
  qSel('.login').style.display = 'none'; // navbar mgt
  qSel('.signUp').style.display = 'none'; // navbar mgt
} else if (checkURL('accountSetting')) {
  routePromise = import(
    /* webpackChunkName: 'accountSetting' */
    './components/accountSetting'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (checkURL('organogram') || window.location.pathname.startsWith('/organogram')) {
  if (qSel('.familyTreeNav')) qSel('.familyTreeNav').style.display = 'none'; // navbar mgt

  routePromise = import(
    /* webpackChunkName: 'organogram' */
    './components/familyTree/index.js'
  )
    .then((module) => module.default)
    .catch((err) => showError(err));
} else if (window.location.pathname === '/allMembers/getProfile') {
  qSel('.familyTreeNav').style.display = 'none'; // navbar mgt
  qSel('.notification_count').style.display = 'none'; // navbar mgt

  // import (
  // import { setCookie } from '../../../node_modules/y/Cookie';
  /* webpackChunkName: 'getProfile' */
  //     /* webpackPrefetch: true */
  //     './components/familyTree/index.js'
  // )
  // .then((module) => module.default)
  //     .catch((err) => showError(err))
}


// Start Alpine only once the DOM is fully parsed. This bundle is included
// before @yield('extra_js'), so page-specific components registered there
// (e.g. familyCodeApprovalForm on /register) would otherwise miss the
// alpine:init event and initialise as empty {}.
const startAlpine = () => routePromise.finally(() => Alpine.start());

// Reels player & Create Modal handler lazy loader for feed & profile pages
const loadReelsModuleIfPresent = () => {
  if (document.getElementById('createReelModal') || document.querySelector('.reels-tray-scroll') || document.getElementById('reelsViewport')) {
    import(
      /* webpackChunkName: 'reels' */
      './components/reels/reelsPlayer'
    ).catch(() => {});
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    startAlpine();
    loadReelsModuleIfPresent();
  }, { once: true });
} else {
  startAlpine();
  loadReelsModuleIfPresent();
}

document.addEventListener('show.bs.modal', (e) => {
  if (e.target && e.target.id === 'createReelModal') {
    import(
      /* webpackChunkName: 'reels' */
      './components/reels/reelsPlayer'
    ).catch(() => {});
  }
});
